/**
 * ToggleCardCarousel — swipeable, keyboard-navigable carousel.
 *
 * Implements the WAI-ARIA Authoring Practices Guide carousel pattern:
 *  - Container is `role="region"` + `aria-roledescription="carousel"`
 *    + `tabIndex={0}` so it is reachable by Tab and arrow keys cycle
 *    slides once focused.
 *  - Each slide is `role="group"` + `aria-roledescription="slide"` +
 *    `aria-label="N of M"` so screen readers announce position.
 *  - Dots are real `<button>`s with `aria-label="Go to slide N"` and
 *    `aria-current="true"` on the active dot.
 *  - ArrowLeft / ArrowRight / Home / End are handled when focus is
 *    anywhere inside the region.
 */

import {
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from '../../i18n/index.js';
import { useSwipeCarousel } from '../../hooks/useSwipeCarousel.js';
import styles from './ToggleCardCarousel.module.css';

export interface ToggleCardCarouselProps {
  cards: ReactNode[];
  autoRotateMs?: number;
  showDots?: boolean;
  /** Accessible label for the carousel region. */
  ariaLabel?: string;
}

export function ToggleCardCarousel({
  cards,
  autoRotateMs = 0,
  showDots = true,
  ariaLabel,
}: ToggleCardCarouselProps): ReactElement | null {
  const { t } = useTranslation();
  const { activeIdx, trackRef, viewportRef, setPaused, goTo } = useSwipeCarousel({
    count: cards.length,
    autoInterval: autoRotateMs,
    loop: false,
  });

  /* ── Keyboard navigation ── */
  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent): void => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(Math.max(0, activeIdx - 1));
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(Math.min(cards.length - 1, activeIdx + 1));
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        goTo(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        goTo(cards.length - 1);
      }
    },
    [activeIdx, cards.length, goTo],
  );

  if (cards.length === 0) return null;

  const regionLabel =
    ariaLabel ?? t('carousel.region', { defaultValue: 'Carousel' });
  const carouselDescription = t('carousel.roleDescription', {
    defaultValue: 'carousel',
  });
  const slideDescription = t('carousel.slideRoleDescription', {
    defaultValue: 'slide',
  });

  return (
    // The WAI-ARIA APG carousel pattern requires `role="region"` +
    // `aria-roledescription="carousel"` with the region itself being
    // focusable so arrow keys cycle slides once tabbed into. The
    // `no-noninteractive-element-interactions` rule fires on the
    // onKeyDown handler attached to a non-interactive role; the rule
    // pre-dates the carousel pattern and there is no roles option to
    // whitelist it. Disable for this single line — the alternative
    // (moving keyboard handling to a hidden interactive proxy) breaks
    // the focus contract documented in the JSDoc.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={styles.container}
      onKeyDown={handleKeyDown}
      onMouseEnter={(): void => setPaused(true)}
      onMouseLeave={(): void => setPaused(false)}
      role="region"
      aria-roledescription={carouselDescription}
      aria-label={regionLabel}
      tabIndex={0}
    >
      <div className={styles.viewport} ref={viewportRef}>
        <div className={styles.track} ref={trackRef}>
          {cards.map((card, index) => {
            const inactive = index !== activeIdx;
            // WAI-ARIA APG carousel pattern requires inactive slides to
            // be removed from the tab order so Tab doesn't reach links
            // / buttons that are visually hidden. `aria-hidden` alone is
            // not enough — focusable descendants stay tabbable. The
            // `inert` attribute (HTML spec, broadly supported in 2026)
            // disables focus + pointer events for the entire subtree.
            return (
              <div
                key={index}
                className={styles.slide}
                role="group"
                aria-roledescription={slideDescription}
                aria-label={t('carousel.slideOf', {
                  defaultValue: '{{current}} of {{total}}',
                  current: index + 1,
                  total: cards.length,
                })}
                aria-hidden={inactive ? true : undefined}
                inert={inactive}
              >
                {card}
              </div>
            );
          })}
        </div>
      </div>

      {showDots && cards.length > 1 && (
        <div className={styles.dotsContainer}>
          {cards.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`${styles.dot} ${index === activeIdx ? styles.dotActive : ''}`}
              onClick={(): void => goTo(index)}
              aria-label={t('carousel.goToSlide', {
                defaultValue: 'Go to slide {{n}}',
                n: index + 1,
              })}
              aria-current={index === activeIdx ? 'true' : 'false'}
            />
          ))}
        </div>
      )}
    </div>
  );
}
